import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../../store/auth';
import { useGuardianPaths } from '../../../../hooks/useGuardianPaths';
import { Button, Card, CardHeader, CardContent } from '../../../../components/ui';
import { lessonService } from '../../../../services/lesson';
import { academyService } from '../../../../services/academy';
import { termService } from '../../../../services/term';
import { courtService } from '../../../../services/court';
import { lessonOverrideService } from '../../../../services/lessonOverride';
import { makeupcreditService } from '../../../../services/makeupCredit';
import { studentService } from '../../../../services/student';
import { waitlistService } from '../../../../services/waitlist';
import { Lesson, Academy, Term, Court, Student, hasAnyRole } from '@court-sync/shared';
import { isLessonBookable, getLessonBookingReason, isLessonFull } from '../../../../utils/lessonBooking';
import toast from 'react-hot-toast';

const DAYS_OF_WEEK = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
];

export default function LessonDetail() {
  const { id } = useParams<{ id: string }>();
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const paths = useGuardianPaths();
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [academy, setAcademy] = useState<Academy | null>(null);
  const [term, setTerm] = useState<Term | null>(null);
  const [court, setCourt] = useState<Court | null>(null);
  const [loading, setLoading] = useState(true);
  const [booking] = useState(false);
  const [termCreditId, setTermCreditId] = useState<string | null>(null);
  const [isTermCancelled, setIsTermCancelled] = useState(false);
  const [usingTermCredit, setUsingTermCredit] = useState(false);
  const [waitlistStudents, setWaitlistStudents] = useState<Student[]>([]);
  const [myWaitlistEntryId, setMyWaitlistEntryId] = useState<string | null>(null);
  const [selectedWaitlistStudent, setSelectedWaitlistStudent] = useState<string | null>(null);
  const [joinWaitlistLoading, setJoinWaitlistLoading] = useState(false);
  const [leaveWaitlistLoading, setLeaveWaitlistLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (!id) {
      navigate('/dashboard');
      return;
    }
    fetchLessonDetails();
  }, [user, navigate, id]);

  const fetchLessonDetails = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const lessonResponse = await lessonService.getLesson(id);
      const lessonData = lessonResponse.data;

      if (!lessonData) {
        toast.error('Lesson not found');
        navigate(paths?.lessons ?? '/academy-picker');
        return;
      }

      setLesson(lessonData);

      // Fetch related data
      const [academyResponse, termResponse, courtResponse] = await Promise.all([
        academyService.getAcademy(lessonData.academyId),
        termService.getTerm(lessonData.termId),
        courtService.getCourt(lessonData.courtId)
      ]);

      setAcademy(academyResponse.data || null);
      setTerm(termResponse.data || null);
      setCourt(courtResponse.data || null);

      // Check if lesson is term-cancelled and if user has a term credit for it
      if (lessonData.academyId && hasAnyRole(user, ['guardian', 'student'])) {
        try {
          const [cancellations, creditId] = await Promise.all([
            lessonOverrideService.getTermCancellations(lessonData.academyId),
            makeupcreditService.getTermCreditForLesson(lessonData.id),
          ]);
          setIsTermCancelled(cancellations.some((tc) => tc.lessonId === lessonData.id));
          setTermCreditId(creditId);
        } catch {
          // Ignore
        }
      }
    } catch (error) {
      console.error('Error fetching lesson details:', error);
      toast.error('Failed to load lesson details');
      navigate(paths?.lessons ?? '/academy-picker');
    } finally {
      setLoading(false);
    }
  };

  const handleBookLesson = async () => {
    if (!lesson || !user) return;

    // Only guardians/students can book lessons
    if (!hasAnyRole(user, ['guardian', 'student'])) {
      toast.error('You need a student or guardian account to book lessons');
      return;
    }

    // Redirect to booking page where student selection and booking creation happens
    navigate(paths?.bookLesson(lesson.id) ?? '/academy-picker');
  };

  const handleUseTermCredit = async () => {
    if (!termCreditId || !lesson) return;
    try {
      setUsingTermCredit(true);
      await makeupcreditService.useTermCredit(termCreditId, lesson.id);
      toast.success('Enrolled for the full term using your credit!');
      navigate(paths?.bookings ?? '/academy-picker');
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to use term credit');
    } finally {
      setUsingTermCredit(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    const loginPath = paths ? `${paths.base}/login` : '/login';
    navigate(loginPath, { replace: true });
  };

  const handleJoinWaitlist = async (studentId: string) => {
    if (!lesson || !term || !academy) return;
    try {
      setJoinWaitlistLoading(true);
      const res = await waitlistService.joinWaitlist({
        lessonId: lesson.id,
        termId: lesson.termId,
        studentId,
        academyId: academy.id,
      });
      const entryId = res.data?.waitlistEntryId ?? res.data?.waitlistEntry?.id;
      if (entryId) setMyWaitlistEntryId(entryId);
      toast.success("You're on the waitlist");
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to join waitlist');
    } finally {
      setJoinWaitlistLoading(false);
    }
  };

  const handleLeaveWaitlist = async (entryId: string) => {
    try {
      setLeaveWaitlistLoading(true);
      await waitlistService.leaveWaitlist(entryId);
      setMyWaitlistEntryId(null);
      toast.success('Left the waitlist');
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to leave waitlist');
    } finally {
      setLeaveWaitlistLoading(false);
    }
  };

  // Fetch waitlist students and check if user is on waitlist when lesson is full and waitlist enabled
  useEffect(() => {
    const full = academy && lesson && isLessonFull(lesson, academy.allowEnrolmentWhenFull);
    if (
      !lesson ||
      !academy?.waitlistEnabled ||
      academy?.allowEnrolmentWhenFull ||
      !hasAnyRole(user, ['guardian', 'student']) ||
      !full
    ) {
      return;
    }
    const loadWaitlistData = async () => {
      try {
        const [studentsRes, entriesRes] = await Promise.all([
          studentService.getStudents(academy.id),
          waitlistService.getMyWaitlistEntries(academy.id),
        ]);
        setWaitlistStudents(studentsRes.data ?? []);
        const myEntry = (entriesRes.data ?? []).find((e) => e.lessonId === lesson.id);
        setMyWaitlistEntryId(myEntry?.id ?? null);
      } catch {
        // Ignore
      }
    };
    loadWaitlistData();
  }, [lesson, academy, user]);

  if (!user) {
    return null;
  }

  if (loading) {
    return (
      <>
        <nav className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
          <div className="contents">
            <div className="flex justify-between h-16">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate(hasAnyRole(user, ['guardian', 'student']) ? (paths?.lessons ?? '/academy-picker') : '/dashboard')}
                  className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                >
                  ← Back to {hasAnyRole(user, ['guardian', 'student']) ? 'Lessons' : 'Dashboard'}
                </button>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Lesson Details
                </h1>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Welcome, {user.firstName}!
                </span>
                <Button variant="outline" size="sm" onClick={handleLogout}>
                  Logout
                </Button>
              </div>
            </div>
          </div>
        </nav>
        <div className="max-w-4xl mx-auto py-2 px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </>
    );
  }

  if (!lesson) {
    return null;
  }

  const isFullyBooked = isLessonFull(lesson, academy?.allowEnrolmentWhenFull);
  const spotsRemaining = lesson.maxCapacity - (lesson.currentEnrollment || 0);
  const isBookable = isLessonBookable(lesson, term);
  const bookingReason = getLessonBookingReason(lesson, term);

  return (
    <>
      {/* Navigation */}
      <nav className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="contents">
          <div className="flex justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(hasAnyRole(user, ['guardian', 'student']) ? (paths?.lessons ?? '/academy-picker') : '/dashboard')}
                className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
              >
                ← Back to {hasAnyRole(user, ['guardian', 'student']) ? 'Lessons' : 'Dashboard'}
              </button>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                Lesson Details
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Welcome, {user.firstName}!
              </span>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto py-2 px-4 sm:px-6 lg:px-8">
        {/* Lesson Header */}
        <div className="mb-8">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-8 text-white">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h1 className="text-3xl font-bold">{lesson.name}</h1>
                  <span className="px-3 py-1 text-sm font-medium rounded-full bg-white bg-opacity-20">
                    {lesson.lessonType}
                  </span>
                </div>
                <p className="text-blue-100 text-lg mb-4">
                  {academy?.name || 'Academy'}
                </p>
                <div className="flex items-center space-x-6 text-blue-100">
                  <div>
                    📅 {DAYS_OF_WEEK[lesson.daysOfWeek?.[0] ?? 0]}s
                  </div>
                  <div>
                    🕐 {lesson.startTime} - {lesson.endTime}
                  </div>
                  <div>
                    👥 {lesson.currentEnrollment || 0}/{lesson.maxCapacity}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold mb-2">${lesson.price}</div>
                <div className="text-blue-100">per lesson</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            {lesson.description && (
              <Card>
                <CardHeader>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    About This Lesson
                  </h2>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                    {lesson.description}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Lesson Details */}
            <Card>
              <CardHeader>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Lesson Information
                </h2>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white mb-1">
                        Schedule
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        {DAYS_OF_WEEK[lesson.daysOfWeek?.[0] ?? 0]}s from {lesson.startTime} to {lesson.endTime}
                      </p>
                    </div>

                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white mb-1">
                        Term
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        {term?.name || 'Unknown Term'}
                      </p>
                      {term && (
                        <p className="text-sm text-gray-500 dark:text-gray-500">
                          {new Date(term.startDate).toLocaleDateString()} - {new Date(term.endDate).toLocaleDateString()}
                        </p>
                      )}
                    </div>

                    {lesson.skillLevel && (
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white mb-1">
                          Skill Level
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 capitalize">
                          {lesson.skillLevel}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="space-y-4">
                    {lesson.ageGroup && (
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white mb-1">
                          Age Group
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 capitalize">
                          {lesson.ageGroup}
                        </p>
                      </div>
                    )}

                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white mb-1">
                        Court
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        {court?.name || 'Court information unavailable'}
                      </p>
                      {court?.surfaceType && (
                        <p className="text-sm text-gray-500 dark:text-gray-500 capitalize">
                          {court.surfaceType} court
                        </p>
                      )}
                    </div>

                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white mb-1">
                        Class Size
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        Maximum {lesson.maxCapacity} students
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Academy Information */}
            {academy && (
              <Card>
                <CardHeader>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    About {academy.name}
                  </h2>
                </CardHeader>
                <CardContent>
                  {academy.description && (
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      {academy.description}
                    </p>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    {academy.address && (
                      <div>
                        <span className="font-medium text-gray-900 dark:text-white">Address:</span>
                        <p className="text-gray-600 dark:text-gray-400">{academy.address}</p>
                      </div>
                    )}

                    {academy.phone && (
                      <div>
                        <span className="font-medium text-gray-900 dark:text-white">Phone:</span>
                        <p className="text-gray-600 dark:text-gray-400">{academy.phone}</p>
                      </div>
                    )}

                    {academy.email && (
                      <div>
                        <span className="font-medium text-gray-900 dark:text-white">Email:</span>
                        <p className="text-gray-600 dark:text-gray-400">{academy.email}</p>
                      </div>
                    )}

                    {academy.website && (
                      <div>
                        <span className="font-medium text-gray-900 dark:text-white">Website:</span>
                        <a
                          href={academy.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          {academy.website}
                        </a>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Booking Sidebar - Show for guardians and self-students */}
          {hasAnyRole(user, ['guardian', 'student']) && (
            <div className="lg:col-span-1">
              <Card className="sticky top-8">
                <CardHeader>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Book This Lesson
                  </h2>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                        ${lesson.price}
                      </div>
                      <p className="text-gray-600 dark:text-gray-400">per lesson</p>
                    </div>

                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-600 dark:text-gray-400">Availability:</span>
                        <span className={`font-medium ${isFullyBooked ? 'text-red-600' : 'text-green-600'}`}>
                          {isFullyBooked ? 'Fully Booked' : `${spotsRemaining} spots left`}
                        </span>
                      </div>

                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all"
                          style={{
                            width: `${((lesson.currentEnrollment || 0) / lesson.maxCapacity) * 100}%`
                          }}
                        ></div>
                      </div>

                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        {lesson.currentEnrollment || 0} of {lesson.maxCapacity} spots filled
                      </p>
                    </div>

                    {termCreditId && isTermCancelled ? (
                      <Button
                        className="w-full"
                        onClick={handleUseTermCredit}
                        disabled={usingTermCredit}
                        isLoading={usingTermCredit}
                      >
                        {usingTermCredit ? 'Enrolling...' : 'Use term credit to re-enrol'}
                      </Button>
                    ) : isFullyBooked && !academy?.allowEnrolmentWhenFull && academy?.waitlistEnabled && lesson.termId ? (
                      myWaitlistEntryId ? (
                        <div className="space-y-2">
                          <p className="text-green-600 dark:text-green-400 font-medium text-center">You&apos;re on the waitlist</p>
                          <Button
                            className="w-full"
                            variant="outline"
                            onClick={() => handleLeaveWaitlist(myWaitlistEntryId)}
                            disabled={leaveWaitlistLoading}
                          >
                            {leaveWaitlistLoading ? 'Leaving...' : 'Leave waitlist'}
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Join waitlist for</label>
                          <select
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-800"
                            value={selectedWaitlistStudent ?? ''}
                            onChange={(e) => setSelectedWaitlistStudent(e.target.value || null)}
                          >
                            <option value="">Select student</option>
                            {waitlistStudents.map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.firstName} {s.lastName}
                                {s.isSelf ? ' (Myself)' : ''}
                              </option>
                            ))}
                          </select>
                          <Button
                            className="w-full"
                            onClick={() => selectedWaitlistStudent && handleJoinWaitlist(selectedWaitlistStudent)}
                            disabled={!selectedWaitlistStudent || joinWaitlistLoading}
                            isLoading={joinWaitlistLoading}
                          >
                            {joinWaitlistLoading ? 'Joining...' : 'Join Waitlist'}
                          </Button>
                        </div>
                      )
                    ) : (
                      <Button
                        className="w-full"
                        onClick={handleBookLesson}
                        disabled={isFullyBooked || booking || !isBookable}
                        isLoading={booking}
                        title={!isBookable ? bookingReason || 'Cannot book this lesson' : undefined}
                      >
                        {booking
                          ? 'Booking...'
                          : isFullyBooked
                            ? 'Fully Booked'
                            : !isBookable
                              ? 'Not Available'
                              : 'Book Now'}
                      </Button>
                    )}

                    <div className="text-xs text-gray-500 dark:text-gray-500 text-center">
                      You'll be able to review your booking before payment
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Lesson Stats Sidebar - Show for academy owners */}
          {hasAnyRole(user, ['academy_owner']) && (
            <div className="lg:col-span-1">
              <Card className="sticky top-8">
                <CardHeader>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Lesson Statistics
                  </h2>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                        ${lesson.price}
                      </div>
                      <p className="text-gray-600 dark:text-gray-400">per lesson</p>
                    </div>

                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-600 dark:text-gray-400">Enrollment:</span>
                        <span className={`font-medium ${isFullyBooked ? 'text-red-600' : 'text-green-600'}`}>
                          {isFullyBooked ? 'Fully Booked' : `${spotsRemaining} spots available`}
                        </span>
                      </div>

                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all"
                          style={{
                            width: `${((lesson.currentEnrollment || 0) / lesson.maxCapacity) * 100}%`
                          }}
                        ></div>
                      </div>

                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        {lesson.currentEnrollment || 0} of {lesson.maxCapacity} spots filled
                      </p>
                    </div>

                    <div className="text-center text-sm text-gray-600 dark:text-gray-400">
                      {isFullyBooked ? 'This lesson is at full capacity' : `${spotsRemaining} spots remaining for booking`}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
